import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { coursesClient } from '../../apis/client-instances';
import { clearResults, setIsSearching, setSearchResults } from '../../features/search/searchSlice';
import { RootState } from '../../types';
import { Box, CircularProgress, Typography } from '../ui';
import SearchBar from './SearchBar';

export default function SearchAutocomplete() {
  const [searchInput, setSearchInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<any>(null);
  const searchRef = useRef<any>(null);
  const debounceTimer = useRef<any>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { results, isSearching } = useSelector((state: RootState) => state.search);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function — hits the server-side Name search so we never
  // pull a giant course list to filter client-side.
  const performSearch = async (query: string) => {
    if (!query.trim()) {
      dispatch(clearResults());
      setIsOpen(false);
      return;
    }

    dispatch(setIsSearching(true));

    try {
      const result = await coursesClient.getCourses(
        undefined,            // id
        query,                // name (server-side filter)
        undefined,            // accountId
        undefined,            // workspaceId
        undefined,            // isDraft
        undefined,            // tagIds
        undefined,            // publishedDateFrom
        undefined,            // publishedDateTo
        undefined,            // myCourses
        undefined,            // isAssigned
        undefined,            // courseType
        0,                    // offset
        25,                   // limit — dropdown shows first 25 matches
        undefined,            // securityWorkspaceId
        undefined             // isPublicOnly
      );
      dispatch(setSearchResults(result));
      setIsOpen((result?.length ?? 0) > 0);
    } catch (error) {
      console.error('Search error:', error);
      dispatch(clearResults());
    } finally {
      dispatch(setIsSearching(false));
    }
  };

  // Handle input change with debounce
  const handleChange = (e: any) => {
    const value = e.target.value;
    setSearchInput(value);
    setHighlightedIndex(-1);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      performSearch(value);
    }, 300); // 300ms delay
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: any) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          handleResultClick(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Handle result click
  const handleResultClick = (course: any) => {
    setSearchInput('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    dispatch(clearResults());
    navigate(`/courses/${course.slug || course.id}`);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }} ref={searchRef}>
      <SearchBar value={searchInput} onChange={handleChange} onKeyDown={handleKeyDown} placeholder="Search courses..." />

      {/* Autocomplete Dropdown */}
      {isOpen && (
        <Box
          ref={dropdownRef}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1000,
          }}
        >
          {isSearching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : results.length > 0 ? (
            results.map((course: any, index: number) => (
              <Box
                key={course.id}
                onClick={() => handleResultClick(course)}
                onMouseEnter={() => setHighlightedIndex(index)}
                sx={{
                  display: 'flex',
                  gap: 2,
                  p: 2,
                  cursor: 'pointer',
                  backgroundColor: highlightedIndex === index ? 'rgba(36, 99, 235, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(36, 99, 235, 0.1)',
                  },
                  borderBottom: index < results.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                }}
              >
                {course.thumbnailUrl && (
                  <Box
                    component="img"
                    src={course.thumbnailUrl}
                    alt={course.title}
                    sx={{
                      width: 80,
                      height: 50,
                      objectFit: 'cover',
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {course.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {course.description}
                  </Typography>
                </Box>
              </Box>
            ))
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No courses found
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
